/**
 * 聊天输入框组件
 *
 * 提供消息输入功能，支持：
 * - 左侧快捷操作按钮（@设定、#章节、图片上传）
 * - 中间文本输入框（自动调整高度）
 * - 右侧模型选择器、语音输入、发送按钮
 * - 淡黄色主题配色
 * - 引用标记显示
 * - 图片预览
 * - Token使用进度条
 */

import React, { useState, useCallback, useRef, useEffect } from 'react';
import { ModelSelector } from './ModelSelector';
import { AgentSelector } from './AgentSelector';
import { SettingsReferenceModal } from './SettingsReferenceModal';
import { ChapterReferenceModal } from './ChapterReferenceModal';
import { AIGeneratedNotice } from '../ui/AIGeneratedNotice';
import { useChatStore } from '../../store/chatStore';
import { useModelStore } from '../../store/modelStore';
import { onTextQuote } from '../../utils/textSelectionEvents';
import type { TextQuoteEventDetail } from '../../types/textSelection';

// 消息长度限制：10KB
const MAX_MESSAGE_LENGTH = 10240;
// 最大高度：200px
const MAX_HEIGHT = 200;
// 最小高度：44px
const MIN_HEIGHT = 44;

/**
 * 模型信息接口
 */
interface ModelInfo {
  name?: string;
  context_length?: number;
  max_tokens?: number;
}

/**
 * ChatInput组件属性接口
 */
interface ChatInputProps {
  /** 当前选中的模型 */
  currentModel: string;
  /** 模型切换回调 */
  onModelChange: (model: string) => void;
  /** 发送消息回调 */
  onSendMessage: (content: string) => Promise<void>;
  /** 是否禁用输入 */
  disabled?: boolean;
  /** 是否正在流式输出 */
  isStreaming?: boolean;
  /** JWT认证令牌 */
  token?: string;
  /** 工作区ID */
  workspaceId?: number;
}

/**
 * @图标（设定引用）
 */
const AtIcon: React.FC<{ className?: string }> = ({ className = 'w-5 h-5' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 8v8m-4-5v5m-4-2v2m-2 4h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
  </svg>
);

/**
 * #图标（章节引用）
 */
const HashIcon: React.FC<{ className?: string }> = ({ className = 'w-5 h-5' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14" />
  </svg>
);

/**
 * 发送图标
 */
const SendIcon: React.FC<{ className?: string }> = ({ className = 'w-5 h-5' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
  </svg>
);

/**
 * 加载图标
 */
const SpinnerIcon: React.FC<{ className?: string }> = ({ className = 'w-5 h-5' }) => (
  <svg className={`animate-spin ${className}`} fill="none" viewBox="0 0 24 24" aria-hidden="true">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
  </svg>
);

/**
 * ChatInput组件
 */
export const ChatInput: React.FC<ChatInputProps> = React.memo(({
  currentModel,
  onModelChange,
  onSendMessage,
  disabled = false,
  isStreaming = false,
  token,
  workspaceId,
}) => {
  // ===== 状态管理 =====
  const [inputValue, setInputValue] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [showChapterModal, setShowChapterModal] = useState(false);
  const [abortedAnimating, setAbortedAnimating] = useState(false);

  // 从 chatStore 获取 token 使用量
  const lastTokensUsed = useChatStore((state) => state.lastTokensUsed);

  // 从 modelStore 获取模型数据
  const modelsInfo = useModelStore((state) => state.modelsInfo);
  const fetchModels = useModelStore((state) => state.fetchModels);

  // Refs
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  /**
   * 获取模型配置（使用 modelStore 缓存）
   */
  useEffect(() => {
    fetchModels();
  }, [fetchModels]);

  /**
   * 格式化 token 数量
   */
  const formatTokens = (tokens: number): string => {
    if (tokens >= 1000000) {
      return `${(tokens / 1000000).toFixed(1)}M`;
    }
    if (tokens >= 1000) {
      return `${(tokens / 1000).toFixed(1)}K`;
    }
    return tokens.toString();
  };

  /**
   * 自动调整textarea高度
   */
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      const scrollHeight = textareaRef.current.scrollHeight;
      const newHeight = Math.min(Math.max(scrollHeight, MIN_HEIGHT), MAX_HEIGHT);
      textareaRef.current.style.height = `${newHeight}px`;
    }
  }, [inputValue]);

  /**
   * 处理输入框内容变化
   */
  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    const encoder = new TextEncoder();
    const byteLength = encoder.encode(value).length;

    if (byteLength > MAX_MESSAGE_LENGTH) {
      // 截断到最大长度
      let truncated = value;
      while (encoder.encode(truncated).length > MAX_MESSAGE_LENGTH) {
        truncated = truncated.slice(0, -1);
      }
      setInputValue(truncated);
    } else {
      setInputValue(value);
    }
  }, []);

  /**
   * 处理发送消息
   */
  const handleSend = useCallback(async () => {
    const content = inputValue.trim();

    if (!content || isSending || isStreaming || disabled) {
      return;
    }

    setIsSending(true);

    try {
      // 立即清空输入（第一时间响应）
      setInputValue('');

      // 重置textarea高度
      if (textareaRef.current) {
        textareaRef.current.style.height = `${MIN_HEIGHT}px`;
      }

      await onSendMessage(content);
    } catch (err) {
      console.error('发送消息失败:', err);
    } finally {
      setIsSending(false);
    }
  }, [inputValue, isSending, isStreaming, disabled, onSendMessage]);

  const handleAbort = useCallback(() => {
    const currentSession = useChatStore.getState().currentSession;
    if (!currentSession?.id) return;

    setIsSending(false);

    try {
      useChatStore.getState().abortMessage(currentSession.id);

      setAbortedAnimating(true);
      setTimeout(() => {
        setAbortedAnimating(false);
      }, 1500);
    } catch (err) {
      console.error('中止消息失败:', err);
    }
  }, []);

  /**
   * 处理键盘事件
   */
  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }, [handleSend]);

  /**
   * 处理插入设定引用
   */
  const handleInsertSettingReference = useCallback((reference: string) => {
    setInputValue((prev) => {
      const textarea = textareaRef.current;
      if (textarea) {
        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const newValue = prev.substring(0, start) + reference + prev.substring(end);
        // 设置光标位置到引用标记之后
        setTimeout(() => {
          textarea.focus();
          textarea.setSelectionRange(start + reference.length, start + reference.length);
        }, 0);
        return newValue;
      }
      return prev + reference;
    });
  }, []);

  /**
   * 处理插入章节引用
   */
  const handleInsertChapterReference = useCallback((reference: string) => {
    setInputValue((prev) => {
      const textarea = textareaRef.current;
      if (textarea) {
        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const newValue = prev.substring(0, start) + reference + prev.substring(end);
        // 设置光标位置到引用标记之后
        setTimeout(() => {
          textarea.focus();
          textarea.setSelectionRange(start + reference.length, start + reference.length);
        }, 0);
        return newValue;
      }
      return prev + reference;
    });
  }, []);

  /**
   * 监听文字选中引用事件
   * 从章节或设定组件触发的引用事件
   */
  useEffect(() => {
    const unsubscribe = onTextQuote((detail: TextQuoteEventDetail) => {
      // 格式化引用内容为 Markdown 引用块格式
      // 例如：
      // #{chapterId}
      // > 引用的文字内容
      const quotedText = `\n${detail.reference}\n> ${detail.text.split('\n').join('\n> ')}\n`;

      // 插入到输入框末尾
      setInputValue((prev) => {
        // 如果当前内容不为空，添加一个换行
        const prefix = prev.trim() ? '\n' : '';
        return prev + prefix + quotedText;
      });

      // 聚焦到输入框
      setTimeout(() => {
        textareaRef.current?.focus();
      }, 0);
    });

    return unsubscribe;
  }, []);

  /**
   * 打开设定引用模态框
   */
  const handleOpenSettingsModal = useCallback(() => {
    if (!disabled && !isStreaming) {
      setShowSettingsModal(true);
    }
  }, [disabled, isStreaming]);

  /**
   * 打开章节引用模态框
   */
  const handleOpenChapterModal = useCallback(() => {
    if (!disabled && !isStreaming) {
      setShowChapterModal(true);
    }
  }, [disabled, isStreaming]);

  // ===== 渲染函数 =====

  const isAIWorking = isStreaming || useChatStore.getState().loading;

  const canSend = inputValue.trim().length > 0 && !isSending && !disabled;

  // 计算 token 使用百分比（使用 context_length 作为上下文窗口限制）
  const currentModelInfo = modelsInfo[currentModel];
  const contextLength = currentModelInfo?.context_length || 128000;
  const tokenPercent = Math.min((lastTokensUsed / contextLength) * 100, 100);
  const showProgressBar = lastTokensUsed > 0;

  return (
    <>
      <div id="chat-input" className="px-4 py-3 border-t border-gray-200 bg-gray-50 flex flex-col gap-3">
        {/* Token使用进度条 */}
        {showProgressBar && (
          <div className="flex items-center gap-2 text-xs text-gray-500">
            <div className="flex-1 h-1.5 bg-gray-200 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-150 ${
                  tokenPercent > 80 ? 'bg-red-500' : tokenPercent > 50 ? 'bg-yellow-500' : 'bg-green-500'
                }`}
                style={{ width: `${tokenPercent}%` }}
              />
            </div>
            <span className="whitespace-nowrap">
              {formatTokens(lastTokensUsed)} / {formatTokens(contextLength)}
            </span>
          </div>
        )}

        {/* 文本输入框（上方） */}
        <div className="relative">
          <textarea
            ref={textareaRef}
            value={inputValue}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            placeholder="请输入创作指令，Shift+Enter换行"
            aria-label="创作指令输入框"
            aria-multiline="true"
            aria-required="false"
            role="textbox"
            className="w-full px-4 py-3 pr-12 bg-white border border-gray-200 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-transparent text-sm text-gray-900 placeholder-gray-400"
            disabled={disabled || isStreaming}
            style={{ minHeight: `${MIN_HEIGHT}px`, maxHeight: `${MAX_HEIGHT}px` }}
          />
          {/* 字符计数 */}
          {inputValue.length > 0 && (
            <div className="absolute bottom-2 right-2 text-xs text-gray-400">
              {inputValue.length} / {MAX_MESSAGE_LENGTH}
            </div>
          )}
        </div>

        {/* 下方操作区域 */}
        <div className="flex flex-col gap-1">
          {/* 工具栏 */}
          <div className="flex items-center justify-between gap-2">
            {/* 左侧：快捷操作按钮 */}
            <div className="flex items-center gap-2 flex-1">
              {/* @设定引用 */}
              <button
                type="button"
                onClick={handleOpenSettingsModal}
                disabled={disabled || isStreaming}
                className="p-2 text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                aria-label="引用设定"
                title="引用设定 (@)"
              >
                <AtIcon />
              </button>

              {/* #章节引用 */}
              <button
                type="button"
                onClick={handleOpenChapterModal}
                disabled={disabled || isStreaming}
                className="p-2 text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                aria-label="引用章节"
                title="引用章节 (#)"
              >
                <HashIcon />
              </button>
            </div>

            {/* 右侧：模型选择器和操作按钮 */}
            <div className="flex items-center gap-2">
              {/* 智能体选择器 */}
              <AgentSelector disabled={disabled || isStreaming} />

              {/* 模型选择器 */}
              <ModelSelector
                currentModel={currentModel}
                onModelChange={onModelChange}
                disabled={disabled || isStreaming}
                token={token}
              />

              {/* 发送/中止按钮 */}
              <button
                type="button"
                onClick={isAIWorking ? handleAbort : handleSend}
                disabled={!canSend && !isAIWorking}
                className={`
                  px-6 py-3 rounded-lg transition-all duration-150 flex items-center gap-2 shadow-sm hover:shadow-sm active:scale-[0.98]
                  ${isAIWorking
                    ? 'bg-red-600 hover:bg-red-700 text-white'
                    : 'bg-ink-800 hover:bg-ink-700 text-white'
                  }
                  ${!canSend && !isAIWorking ? 'bg-agent-300 cursor-not-allowed' : ''}
                  ${abortedAnimating ? 'bg-green-600' : ''}
                `}
                aria-label={isAIWorking ? '中止消息' : '发送消息'}
              >
                {abortedAnimating ? (
                  <span className="text-sm font-medium">已中止</span>
                ) : isAIWorking ? (
                  <span className="text-sm font-medium">中止</span>
                ) : (
                  <>
                    {isSending ? <SpinnerIcon className="w-4 h-4" /> : <SendIcon className="w-4 h-4" />}
                    <span className="text-sm font-medium">发送</span>
                  </>
                )}
              </button>
            </div>
          </div>

          {/* AI生成提示 */}
          <div className="text-center">
            <AIGeneratedNotice variant="block" className="py-0" />
          </div>
        </div>
      </div>

      {/* 设定引用模态框 */}
      {workspaceId && token && (
        <SettingsReferenceModal
          isOpen={showSettingsModal}
          onClose={() => setShowSettingsModal(false)}
          onSelectSetting={handleInsertSettingReference}
          token={token}
          workspaceId={workspaceId}
        />
      )}

      {/* 章节引用模态框 */}
      {workspaceId && token && (
        <ChapterReferenceModal
          isOpen={showChapterModal}
          onClose={() => setShowChapterModal(false)}
          onSelectChapter={handleInsertChapterReference}
          token={token}
          workspaceId={workspaceId}
        />
      )}
    </>
  );
});

ChatInput.displayName = 'ChatInput';

export default ChatInput;
